import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedUsers, CreateBusinessUserPayload, PaginatedClosedUsersWithBalance } from '../interfaces';
import { Config } from '../app.configs';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { User } from '../models';
import { AuthService } from './auth.service';
import { CacheService } from './cache.service';

export interface ProofOfAddressResponse {
  proof_of_address: string;
  proof_of_address_status: string;
  proof_of_address_failure_reason?: string;
  history_id?: number;
  history_date?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly CACHE_PREFIX_USER = 'user:';
  private readonly CACHE_PREFIX_LIMITS = 'user-limits:';
  private userMemoryMap = new Map<string, User>();

  setCachedUser(user: User): void {
    if (user && user.uuid) {
      this.userMemoryMap.set(user.uuid, user);
    }
  }

  getCachedUser(uuid: string): User | undefined {
    return this.userMemoryMap.get(uuid);
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getUsers(
    page: number = 1,
    search: string = '',
    kycStatus: string = '',
    bridgeStatus: string = '',
    startDate: string = '',
    endDate: string = '',
    hasMultipleKycAttempts: boolean = false,
    noahStatus: string = '',
    hasBeenFrozen: boolean = false,
    proofOfAddressStatus: string = '',
  ): Observable<PaginatedUsers> {
    const params = new HttpParams()
      .set('page', page)
      .set('search', search)
      .set('kyc_status', kycStatus)
      .set('start_date', startDate)
      .set('end_date', endDate)
      .set('has_multiple_kyc_attempts', hasMultipleKycAttempts)
      .set('bridge_status', bridgeStatus)
      .set('noah_status', noahStatus)
      .set('has_been_frozen', hasBeenFrozen)
      .set('proof_of_address_status', proofOfAddressStatus);
    const headers = this.authService.getHeaders();

    return this.http.get<PaginatedUsers>(Config.toApiUrl(`dashboard/users/`), {
      headers,
      params,
      withCredentials: true,
    }).pipe(
      tap((res) => {
        if (res && res.results) {
          res.results.forEach((user) => this.setCachedUser(user));
        }
      }),
    );
  }

  getClosedUsersWithBalance(
    page: number = 1,
    pageSize: number = 20,
    search: string = '',
  ): Observable<PaginatedClosedUsersWithBalance> {
    let params = new HttpParams()
      .set('page', page)
      .set('page_size', pageSize);

    if (search && search.trim().length > 0) {
      params = params.set('search', search.trim());
    }

    const headers = this.authService.getHeaders();

    return this.http.get<PaginatedClosedUsersWithBalance>(
      Config.toApiUrl('dashboard/closed-users-with-balance/'),
      {
        headers,
        params,
        withCredentials: true,
      },
    );
  }


  getUser(uuid: string, forceRefresh: boolean = false): Observable<User> {
    const cacheKey = `${this.CACHE_PREFIX_USER}${uuid}`;

    return this.cacheService.get<User>(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http
          .get<any>(Config.toApiUrl(`dashboard/users/${uuid}/`), {
            headers,
            withCredentials: true,
          })
          .pipe(
            map((data) => {
              if (data) {
                if (
                  data.address &&
                  (!data.addresses || data.addresses.length === 0)
                ) {
                  data.addresses = [data.address];
                } else if (
                  data.address &&
                  data.addresses &&
                  data.addresses.length > 0
                ) {
                  data.addresses[0] = {
                    ...data.addresses[0],
                    ...data.address,
                  };
                }
                return new User(data);
              }
              return new User();
            }),
          );
      },
      undefined,
      forceRefresh,
    ).pipe(
      tap((user) => {
        if (user) {
          this.setCachedUser(user);
        }
      }),
    );
  }

  /**
   * Invalidate cached user data. Call after mutations that modify user state.
   */
  invalidateUserCache(uuid: string): void {
    this.cacheService.invalidate(`${this.CACHE_PREFIX_USER}${uuid}`);
    this.userMemoryMap.delete(uuid);
  }

  /**
   * Invalidate cached transaction limits. Call after limit mutations.
   */
  invalidateLimitsCache(uuid: string): void {
    this.cacheService.invalidate(`${this.CACHE_PREFIX_LIMITS}${uuid}`);
  }

  /**
   * Invalidate all user-related caches.
   */
  invalidateAllUserCaches(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_USER);
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_LIMITS);
  }

  uploadIdentification(
    uuid: string,
    id_document_front: string,
    id_document_back: string,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      id_document_front: id_document_front,
      id_document_back: id_document_back,
    };

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/aiprise-verifications/`),
      payload,
      { headers, withCredentials: true },
    );
  }

  updateKYCAttempts(uuid: string, max_tentatives: number): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      max_tentatives: max_tentatives,
    };

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/aiprise-configurations/`),
      payload,
      { headers, withCredentials: true },
    );
  }

  freeze(
    uuid: string,
    reason: string,
    fullFreeze: boolean = false,
    notifyUser: boolean = true,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/freeze/`),
        { reason, full_freeze: fullFreeze, notify_user: notifyUser },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  unfreeze(uuid: string, reason: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/unfreeze/`),
        { reason },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  updateKYCForm(
    uuid: string,
    category: string,
    answer: string,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      category: category,
      answer: answer,
    };

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/kyc-form-answers/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  updateUser(
    uuid: string,
    first_name: string,
    last_name: string,
    state: string,
    postal_code: string,
    issued_date?: string,
    expiry_date?: string,
    street?: string,
    city?: string,
    id_document_number?: string,
    phone_number_prefix?: string,
    phone_number?: string,
    country?: string,
    proof_of_address?: string,
    proof_of_address_status?: string,
    proof_of_address_failure_reason?: string,
    nationality?: string,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      first_name: first_name,
      last_name: last_name,
      state: state,
      postal_code: postal_code,
      issued_date: issued_date,
      expiry_date: expiry_date,
      street: street,
      city: city,
      id_document_number: id_document_number,
      phone_number_prefix: phone_number_prefix,
      phone_number: phone_number,
      country: country,
      proof_of_address: proof_of_address,
      proof_of_address_status: proof_of_address_status,
      proof_of_address_failure_reason: proof_of_address_failure_reason,
      nationality: nationality,
    };

    return this.http
      .patch(Config.toApiUrl(`dashboard/users/${uuid}/updates/`), payload, {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  activateWallet(uuid: string, wallet_uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(
          `dashboard/users/${uuid}/wallets/${wallet_uuid}/activate/`,
        ),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  deactivateWallet(uuid: string, wallet_uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(
          `dashboard/users/${uuid}/wallets/${wallet_uuid}/deactivate/`,
        ),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  createUsdAccount(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/virtual-accounts/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  bulkFreeze(emails: string[]): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/bulk-freeze-users/`),
        { emails },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateAllUserCaches()));
  }

  migrateToNoah(emails: string[]): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/bulk-migrate-users-to-noah/`),
        { emails },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateAllUserCaches()));
  }

  bulkCloseAccounts(emails: string[]): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/bulk-close-bridge-accounts/`),
        { emails },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateAllUserCaches()));
  }

  doAction(
    uuid: string,
    action: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    const body = { action, ...payload };

    return this.http
      .post(Config.toApiUrl(`dashboard/users/${uuid}/actions/`), body, {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  bypassUniqueness(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/bypass-uniqueness/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  redoKyc(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/redo-kyc/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  getTransactionLimits(
    uuid: string,
    forceRefresh: boolean = false,
  ): Observable<any> {
    const cacheKey = `${this.CACHE_PREFIX_LIMITS}${uuid}`;

    return this.cacheService.get(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get(
          Config.toApiUrl(`dashboard/users/${uuid}/transaction-limits/`),
          { headers, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  approvePendingLimitUpgrade(
    uuid: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/transaction-limits/approve/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateLimitsCache(uuid)));
  }

  rejectPendingLimitUpgrade(
    uuid: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/transaction-limits/reject/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateLimitsCache(uuid)));
  }

  createOutboundLimit(
    uuid: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(
          `dashboard/users/${uuid}/transaction-limits/add-single-outbound/`,
        ),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateLimitsCache(uuid)));
  }

  bypassOpeningFee(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/bypass-opening-fee/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  createMonthlyInboundLimit(
    uuid: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(
          `dashboard/users/${uuid}/transaction-limits/add-monthly-inbound/`,
        ),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateLimitsCache(uuid)));
  }

  deleteTransactionLimit(limitId: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/transaction-limits/${limitId}/delete/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(
        tap(() =>
          this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_LIMITS),
        ),
      );
  }

  transferFundsToUser(
    uuid: string,
    payload: Record<string, any> = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(Config.toApiUrl(`dashboard/users/${uuid}/transfer/`), payload, {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateUserCache(uuid)));
  }

  downloadAccountStatement(
    uuid: string,
    startDate: string,
    endDate: string,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http.post(
      Config.toApiUrl(
        `dashboard/users/${uuid}/download-account-statement/?start_date=${startDate}&end_date=${endDate}`,
      ),
      {},
      { headers, withCredentials: true },
    );
  }

  sendPhoneVerificationOTP(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/send-phone-verification-otp/`),
      {},
      { headers, withCredentials: true },
    );
  }

  migrateUser(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/migrate/`),
      {},
      { headers, withCredentials: true },
    );
  }

  requestBridgeTos(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/request-tos/`),
      {},
      { headers, withCredentials: true },
    );
  }

  createBusinessUser(
    uuid: string,
    payload: CreateBusinessUserPayload,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http.post(
      Config.toApiUrl(`dashboard/users/${uuid}/business-info/`),
      payload,
      { headers, withCredentials: true },
    );
  }

  getProofOfAddress(
    userUuid: string,
    historyId?: number,
  ): Observable<ProofOfAddressResponse> {
    let params = new HttpParams();
    if (historyId) {
      params = params.set("history_id", historyId.toString());
    }
    const headers = this.authService.getHeaders();

    return this.http.get<ProofOfAddressResponse>(
      Config.toApiUrl(`dashboard/users/${userUuid}/proof-of-address/`),
      { headers, params, withCredentials: true },
    );
  }

  manageProofOfAddress(
    userUuid: string,
    payload: {
      action: 'verify_proof' | 'reject_proof';
      reason?: string;
      comment?: string;
    },
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${userUuid}/proof-of-address/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUserCache(userUuid)));
  }
}

